import { useState, type FormEvent } from "react";
import { Navigate, useNavigate } from "react-router";
import { homeFor, useAuth } from "../lib/auth";
import { errorMessage, supabase } from "../lib/supabase";
import { Button, ErrorNote, Field, Input, Mark } from "../components/ui";
import { Logo } from "../components/AppShell";

function AuthFrame({ title, subtitle, children }: { title: string; subtitle: string; children: React.ReactNode }) {
  const landing = import.meta.env.VITE_LANDING_URL as string | undefined;
  return (
    <div className="flex min-h-dvh flex-col bg-bone-parchment">
      <header className="mx-auto flex w-full max-w-[1200px] items-center justify-between px-24 py-24">
        <Logo />
        {landing && <a href={landing} className="text-base15 font-medium no-underline">Ir al sitio de la escuela</a>}
      </header>
      <main className="flex flex-1 items-start justify-center px-24 pt-64 pb-96">
        <div className="w-full max-w-[420px]">
          <div className="mb-32">
            <Mark className="mb-16" />
            <h1 className="font-serif text-heading">{title}</h1>
            <p className="mt-8 text-base16 text-ashen">{subtitle}</p>
          </div>
          <div className="rounded-3xl border border-chalk bg-paper-white p-32">{children}</div>
        </div>
      </main>
    </div>
  );
}

export function LoginPage() {
  const { session, profile, loading } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [mode, setMode] = useState<"password" | "link">("password");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [sent, setSent] = useState(false);

  if (!loading && session && profile) return <Navigate to={profile.onboarding_completed ? homeFor(profile.role) : "/bienvenida"} replace />;

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      if (mode === "password") {
        const { error } = await supabase.auth.signInWithPassword({ email: email.trim(), password });
        if (error) throw new Error(error.message === "Invalid login credentials" ? "Correo o contraseña incorrectos" : error.message);
      } else {
        const { error } = await supabase.auth.signInWithOtp({
          email: email.trim(),
          options: { shouldCreateUser: false, emailRedirectTo: `${window.location.origin}/` },
        });
        if (error) throw error;
        setSent(true);
      }
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <AuthFrame title="Entra a tu escritorio" subtitle="Plataforma de Escritura Integral de la Escuela Libre de Escritores.">
      {sent ? (
        <p className="text-base16 text-graphite">Te enviamos un enlace de acceso a <strong className="font-w550">{email}</strong>. Ábrelo desde este dispositivo.</p>
      ) : (
        <form onSubmit={submit} className="flex flex-col gap-16">
          <Field label="Correo">
            <Input type="email" autoComplete="email" required value={email} onChange={(e) => setEmail(e.target.value)} />
          </Field>
          {mode === "password" && (
            <Field label="Contraseña">
              <Input type="password" autoComplete="current-password" required value={password} onChange={(e) => setPassword(e.target.value)} />
            </Field>
          )}
          <ErrorNote>{error}</ErrorNote>
          <Button type="submit" loading={busy} className="mt-8">
            {mode === "password" ? "Entrar" : "Enviarme un enlace"}
          </Button>
          <button type="button" onClick={() => setMode(mode === "password" ? "link" : "password")} className="text-body text-graphite underline-offset-4 hover:underline">
            {mode === "password" ? "Prefiero entrar con un enlace a mi correo" : "Entrar con contraseña"}
          </button>
          <p className="text-xs2 text-ashen">El acceso es por invitación. Si eres alumno y no la recibiste, escribe a coordinación.</p>
        </form>
      )}
    </AuthFrame>
  );
}

/** Primera entrada desde el correo de invitación: define contraseña y nombre. */
export function WelcomePage() {
  const { session, profile, loading, refresh } = useAuth();
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  if (loading) return null;
  if (!session) return <Navigate to="/entrar" replace />;
  if (!profile) return <NoProfile />;

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (password.length < 8) return setError("La contraseña debe tener al menos 8 caracteres");
    setBusy(true);
    setError("");
    try {
      const { error: pErr } = await supabase.auth.updateUser({ password });
      if (pErr) throw pErr;
      const fullName = name.trim() || profile!.full_name;
      const done = profile!.role !== "alumno"; // el alumno termina en el onboarding
      const { error: uErr } = await supabase.from("profiles").update({ full_name: fullName, onboarding_completed: done }).eq("id", profile!.id);
      if (uErr) throw uErr;
      await refresh();
      navigate(profile!.role === "alumno" ? "/inicio-de-obra" : homeFor(profile!.role), { replace: true });
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <AuthFrame title="Te damos la bienvenida a PEI" subtitle="Define tu contraseña para entrar después sin enlace.">
      <form onSubmit={submit} className="flex flex-col gap-16">
        <Field label="Tu nombre como aparecerá a tus profesores">
          <Input defaultValue={profile.full_name} onChange={(e) => setName(e.target.value)} autoComplete="name" />
        </Field>
        <Field label="Contraseña" hint="Mínimo 8 caracteres.">
          <Input type="password" autoComplete="new-password" required value={password} onChange={(e) => setPassword(e.target.value)} />
        </Field>
        <ErrorNote>{error}</ErrorNote>
        <Button type="submit" loading={busy} className="mt-8">Continuar</Button>
      </form>
    </AuthFrame>
  );
}

export function NoProfile() {
  const { signOut } = useAuth();
  return (
    <AuthFrame title="Tu cuenta aún no está activa" subtitle="Tu correo no tiene una invitación vigente en PEI.">
      <p className="text-base16 text-graphite">Pide a coordinación de la escuela que te envíe la invitación a este correo.</p>
      <Button variant="secondary" className="mt-24" onClick={signOut}>Cerrar sesión</Button>
    </AuthFrame>
  );
}
